import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Stack } from "@similarweb/ui-components/foundations";

import { ManagementModal } from "app/pages/sales-intelligence/common-components/modals/ManagementModal/ManagementModal";
import { MANAGEMENT_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import usePreventLeavePage from "app/pages/sales-intelligence/hooks/usePreventLeavePage";
import { useUpdateExtensionSettingsMutation } from "app/pages/sales-intelligence/modules/extension-management/query/mutations/useUpdateExtensionSettingsMutation";
import { useExtensionSettingsQuery } from "app/pages/sales-intelligence/modules/extension-management/query/queries/useExtensionSettingsQuery";
import extensionManagementTrackingService from "app/pages/sales-intelligence/modules/extension-management/tracking/trackingService";
import { AiWorkflowsSection } from "app/pages/sales-intelligence/pages/management/components/AiWorkflowsSection/AiWorkflowsSection";
import { DataEnrichmentSection } from "app/pages/sales-intelligence/pages/management/components/DataEnrichmentSection/DataEnrichmentSection";
import { ExtensionManagementBanner } from "app/pages/sales-intelligence/pages/management/components/ExtensionManagementBanner/ExtensionManagementBanner";
import { ExtensionSettingsFooter } from "app/pages/sales-intelligence/pages/management/components/ExtensionSettingsFooter/ExtensionSettingsFooter";
import { FloatingIconSection } from "app/pages/sales-intelligence/pages/management/components/FloatingIconSection/FloatingIconSection";
import { OverviewWidgetsSection } from "app/pages/sales-intelligence/pages/management/components/OverviewWidgetsSection/OverviewWidgetsSection";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";

import { CUSTOM_DETAILS_STYLES, CUSTOM_MODAL_STYLES } from "../../../manage-integration/styles/styles";
import { WrappedWithManagementPreloader } from "../../components/common/WrappedWithManagementPreloader/WrappedWithManagementPreloader";
import { SettingsSectionBlock } from "../../components/SettingsSectionBlock/SettingsSectionBlock";
import { useExtensionSettings } from "../../hooks/useExtensionSettings";

export const ConfigurationTab = () => {
    const { translate } = useTranslate();
    const { data: initialSettings, isLoading } = useExtensionSettingsQuery();

    const { mutate, isPending: isSaving } = useUpdateExtensionSettingsMutation();

    const {
        settings,
        hasChanges,
        toggleField,
        toggleLinkedin,
        toggleCrmCard,
        toggleGoogleWorkspace,
        toggleGoogleWorkspaceChild,
        toggleAutoOpenLinkedin,
        reorderWidgets,
        toggleWidget,
        handleCancel,
        handleSave,
    } = useExtensionSettings({
        initialSettings,
        onSave(draft, { onSuccess }) {
            mutate(draft, { onSuccess });
        },
    });

    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

    const { setConfirm: confirmLeavePage, resetRedirectToState } = usePreventLeavePage(
        hasChanges,
        () => setIsDiscardModalOpen(true),
        [],
        { name: MANAGEMENT_ROUTE, params: {} },
    );

    const handleCancelClick = () => {
        extensionManagementTrackingService.trackCancel();
        handleCancel();
        resetRedirectToState();
        void siNavigator.goToManagementPage();
    };

    const handleSaveClick = () => {
        extensionManagementTrackingService.trackSave();
        handleSave();
    };

    const handleDiscardConfirm = () => {
        setIsDiscardModalOpen(false);
        handleCancel();
        confirmLeavePage(true);
    };

    const handleDiscardClose = () => {
        setIsDiscardModalOpen(false);
        resetRedirectToState();
    };

    return (
        <>
            <Stack
                gap="20px"
                margin="20px 0 40px"
            >
                <ExtensionManagementBanner />
                <WrappedWithManagementPreloader isLoading={isLoading}>
                    <SettingsSectionBlock dataAutomation="sales-intelligence-extension-settings">
                        <FloatingIconSection
                            settings={settings}
                            onToggle={() => toggleField("showFloatingBadge")}
                        />
                    </SettingsSectionBlock>
                    <SettingsSectionBlock dataAutomation="sales-intelligence-extension-floating-icon">
                        <AiWorkflowsSection
                            settings={settings}
                            onToggle={toggleField}
                        />
                        <DataEnrichmentSection
                            settings={settings}
                            onToggleLinkedin={toggleLinkedin}
                            onToggleCrmCard={toggleCrmCard}
                            onToggleGoogleWorkspace={toggleGoogleWorkspace}
                            onToggleGoogleWorkspaceChild={toggleGoogleWorkspaceChild}
                            onToggleAutoOpenLinkedin={toggleAutoOpenLinkedin}
                            onToggleAutoOpenCrm={toggleField}
                        />
                    </SettingsSectionBlock>
                    <SettingsSectionBlock dataAutomation="sales-intelligence-extension-overview-widgets-container">
                        <OverviewWidgetsSection
                            widgets={settings.overviewWidgets}
                            onReorder={reorderWidgets}
                            onToggle={toggleWidget}
                        />
                    </SettingsSectionBlock>
                </WrappedWithManagementPreloader>
            </Stack>
            {isLoading ? null : (
                <ExtensionSettingsFooter
                    hasChanges={hasChanges}
                    isSaving={isSaving}
                    onCancel={handleCancelClick}
                    onSave={handleSaveClick}
                />
            )}
            <ManagementModal
                customStyles={CUSTOM_MODAL_STYLES}
                customDetailsStyles={CUSTOM_DETAILS_STYLES}
                isOpen={isDiscardModalOpen}
                onConfirm={handleDiscardConfirm}
                onClose={handleDiscardClose}
                title={translate("si.management.extension.discard_modal.title")}
                details={translate("si.management.extension.discard_modal.body")}
                confirmBtnText={translate(
                    "si.management.sf.settings_export.modal.discard_fields.notification.btn.confirm.label",
                )}
                cancelBtnType="flat"
                cancelBtnText={translate(
                    "si.management.sf.settings_export.modal.discard_fields.notification.btn.cancel.label",
                )}
            />
        </>
    );
};
